// Third-party library imports
import { Amplify, ResourcesConfig } from "aws-amplify";
import { fetchAuthSession } from "@aws-amplify/auth";
import { cognitoUserPoolsTokenProvider } from "@aws-amplify/auth/cognito";
import { AuthConfig, OAuthConfig } from "@aws-amplify/core";
import { CookieStorage } from "aws-amplify/utils";
import { isEmpty } from "lodash-es";

// Constants
import {
    API_NAME,
    API_ENDPOINT_DEV,
    AWS_REGION_SE_2,
    API_ENDPOINT_TEST,
    API_ENDPOINT_PRETEST,
    API_ENDPOINT_UAT,
    API_ENDPOINT_NONPROD,
    API_ENDPOINT_PROD,
    APPSYNC_ENDPOINT_TEST,
    APPSYNC_ENDPOINT_PRETEST,
    APPSYNC_ENDPOINT_DEV,
    APPSYNC_ENDPOINT_UAT,
    APPSYNC_ENDPOINT_NONPROD,
    APPSYNC_ENDPOINT_PROD,
    USERPOOL_ID_DEV,
    USERPOOL_WEBCLIENT_ID_DEV,
    USERPOOL_ID_TEST,
    USERPOOL_ID_PRETEST,
    USERPOOL_WEBCLIENT_ID_PRETEST,
    USERPOOL_WEBCLIENT_ID_TEST,
    USERPOOL_ID_UAT,
    USERPOOL_WEBCLIENT_ID_UAT,
    USERPOOL_ID_NONPROD,
    USERPOOL_WEBCLIENT_ID_NONPROD,
    USERPOOL_ID_PROD,
    USERPOOL_WEBCLIENT_ID_PROD,
    IDENTITY_POOL_ID_DEV,
    IDENTITY_POOL_ID_TEST,
    IDENTITY_POOL_ID_PRETEST,
    IDENTITY_POOL_ID_UAT,
    IDENTITY_POOL_ID_NONPROD,
    IDENTITY_POOL_ID_PROD,
    AWS_APPSYNC_AUTH_TYPE_API_KEY,
    AWS_APPSYNC_AUTH_MODE,
    SYSTEM_DOMAIN_DEV,
    SYSTEM_DOMAIN_TEST,
    SYSTEM_DOMAIN_PRETEST,
    SYSTEM_DOMAIN_UAT,
    SYSTEM_DOMAIN_NONPROD,
    SYSTEM_DOMAIN_PROD,
} from "./config/config";

// Types & interfaces
import { DynamicObject } from "./utils/commonInterfaces";

export enum Environments {
    DEV = 'dev',
    PROD = 'prod',
    TEST = 'test',
    UAT = 'uat',
    NONPROD = 'nonprod',
    PRETEST = 'pretest',
}

const AplicationName: string = 'IODMConnect';

export const getApplicationName = () => {
    return `${AplicationName}-${AplicationName}`;
}

export const getApiAuthorizationHeader = async () => {
    let accessToken: string = '';
    try {
        const currentSession: DynamicObject = await fetchAuthSession();
        accessToken = currentSession.tokens.accessToken.toString();
    } catch (error) {
        console.error('Error fetching auth session:', error);
    }

    return accessToken;
};

export const getOAuthConfig = (environment: Environments, customProvider: string[]) => {
    const oauthConfig: OAuthConfig = {
        domain: SYSTEM_DOMAIN_DEV,
        scopes: ['email', 'openid', 'profile', 'aws.cognito.signin.user.admin'],
        redirectSignIn: [
            window.location.origin, 
            `${window.location.origin}/auth`
        ],
        redirectSignOut: [
            window.location.origin,
            `${window.location.origin}/auth`
        ],
        responseType: 'code',
        providers: customProvider.map((provider) => ({
            custom: provider,
        })),
    };
    switch (environment) {
        case Environments.TEST:
            oauthConfig.domain = SYSTEM_DOMAIN_TEST;
            break;
        case Environments.PRETEST:
            oauthConfig.domain = SYSTEM_DOMAIN_PRETEST;
            break;
        case Environments.UAT:
            oauthConfig.domain = SYSTEM_DOMAIN_UAT;
            break;
        case Environments.NONPROD:
            oauthConfig.domain = SYSTEM_DOMAIN_NONPROD;
            break;
        case Environments.PROD:
            oauthConfig.domain = SYSTEM_DOMAIN_PROD;
            break;
    }
    return oauthConfig;
}

export const generateAmplifyConfig = (
    configData: DynamicObject, 
    identityProviderConfig?: DynamicObject[]
): ResourcesConfig => {
    const environment = getCurrentBuildEnvironment();
    const apiGatewayUrlUsed: string | undefined = configData.Url || undefined;
    const appsyncUrlUsed: string | undefined = configData.Url
        ? `${configData.Url}/graphql`
        : undefined;

    let storedData = {
        AppsyncUrl: appsyncUrlUsed || APPSYNC_ENDPOINT_DEV,
        ApiGatewayUrl: apiGatewayUrlUsed || API_ENDPOINT_DEV,
        UserPoolId: USERPOOL_ID_DEV,
        UserPoolWebClientId: USERPOOL_WEBCLIENT_ID_DEV,
        IdentityPoolId: IDENTITY_POOL_ID_DEV,
    };

    switch (environment) {
        case Environments.TEST:
            storedData = {
                AppsyncUrl: appsyncUrlUsed || APPSYNC_ENDPOINT_TEST,
                ApiGatewayUrl: apiGatewayUrlUsed || API_ENDPOINT_TEST,
                UserPoolId: USERPOOL_ID_TEST,
                UserPoolWebClientId: USERPOOL_WEBCLIENT_ID_TEST,
                IdentityPoolId: IDENTITY_POOL_ID_TEST,
            };
            break;
        case Environments.PRETEST:
            storedData = {
                AppsyncUrl: appsyncUrlUsed || APPSYNC_ENDPOINT_PRETEST,
                ApiGatewayUrl: apiGatewayUrlUsed || API_ENDPOINT_PRETEST,
                UserPoolId: USERPOOL_ID_PRETEST,
                UserPoolWebClientId: USERPOOL_WEBCLIENT_ID_PRETEST,
                IdentityPoolId: IDENTITY_POOL_ID_PRETEST,
            };
            break;
        case Environments.UAT:
            storedData = {
                AppsyncUrl: appsyncUrlUsed || APPSYNC_ENDPOINT_UAT,
                ApiGatewayUrl: apiGatewayUrlUsed || API_ENDPOINT_UAT,
                UserPoolId: USERPOOL_ID_UAT,
                UserPoolWebClientId: USERPOOL_WEBCLIENT_ID_UAT,
                IdentityPoolId: IDENTITY_POOL_ID_UAT,
            };
            break;
        case Environments.NONPROD:
            storedData = {
                AppsyncUrl: appsyncUrlUsed || APPSYNC_ENDPOINT_NONPROD,
                ApiGatewayUrl: apiGatewayUrlUsed || API_ENDPOINT_NONPROD,
                UserPoolId: USERPOOL_ID_NONPROD,
                UserPoolWebClientId: USERPOOL_WEBCLIENT_ID_NONPROD,
                IdentityPoolId: IDENTITY_POOL_ID_NONPROD,
            };
            break;
        case Environments.PROD:
            storedData = {
                AppsyncUrl: appsyncUrlUsed || APPSYNC_ENDPOINT_PROD,
                ApiGatewayUrl: apiGatewayUrlUsed || API_ENDPOINT_PROD,
                UserPoolId: USERPOOL_ID_PROD,
                UserPoolWebClientId: USERPOOL_WEBCLIENT_ID_PROD,
                IdentityPoolId: IDENTITY_POOL_ID_PROD,
            };
            break;
    };

    const cognitoConfig: Record<string, any> = {
        userPoolId: storedData.UserPoolId,
        userPoolClientId: storedData.UserPoolWebClientId,
    };

    if (storedData.IdentityPoolId) {
        cognitoConfig.identityPoolId = storedData.IdentityPoolId;
    }

    const authConfig: AuthConfig = {
        Cognito: cognitoConfig as any,
    };

    if (identityProviderConfig && !isEmpty(identityProviderConfig)) {
        const oauthConfig = getOAuthConfig(environment, identityProviderConfig as unknown as string[]);
        authConfig.Cognito.loginWith = {
            oauth: oauthConfig
        };
    }

    const amplifyConfig: ResourcesConfig = {
        Auth: authConfig,
        API: {
            GraphQL: {
                endpoint: storedData.AppsyncUrl,
                region: configData.AwsRegion,
                defaultAuthMode: AWS_APPSYNC_AUTH_MODE,
            },
            REST: {
                [API_NAME]: {
                    endpoint: storedData.ApiGatewayUrl,
                    region: configData.AwsRegion,
                },
            }
            
        },
    };

    return amplifyConfig;
};

export const generateAmplifyPublicConfig = (configData: DynamicObject): ResourcesConfig => {
    const environment = getCurrentBuildEnvironment();
    const apiGatewayUrlUsed: string | undefined = configData.Url || undefined;
    const appsyncUrlUsed: string | undefined = configData.Url
        ? `${configData.Url}/graphql`
        : undefined;
    let storedData = {
        AppsyncUrl: appsyncUrlUsed || APPSYNC_ENDPOINT_DEV,
        ApiGatewayUrl: apiGatewayUrlUsed || API_ENDPOINT_DEV,
    };

    switch (environment) {
        case Environments.TEST:
            storedData = {
                AppsyncUrl: appsyncUrlUsed || APPSYNC_ENDPOINT_TEST,
                ApiGatewayUrl: apiGatewayUrlUsed || API_ENDPOINT_TEST,
            };
            break;
        case Environments.PRETEST:
            storedData = {
                AppsyncUrl: appsyncUrlUsed || APPSYNC_ENDPOINT_PRETEST,
                ApiGatewayUrl: apiGatewayUrlUsed || API_ENDPOINT_PRETEST,
            };
            break;
        case Environments.UAT:
            storedData = {
                AppsyncUrl: appsyncUrlUsed || APPSYNC_ENDPOINT_UAT,
                ApiGatewayUrl: apiGatewayUrlUsed || API_ENDPOINT_UAT,
            };
            break;
        case Environments.NONPROD:
            storedData = {
                AppsyncUrl: appsyncUrlUsed || APPSYNC_ENDPOINT_NONPROD,
                ApiGatewayUrl: apiGatewayUrlUsed || API_ENDPOINT_NONPROD,
            };
            break;
        case Environments.PROD:
            storedData = {
                AppsyncUrl: appsyncUrlUsed || APPSYNC_ENDPOINT_PROD,
                ApiGatewayUrl: apiGatewayUrlUsed || API_ENDPOINT_PROD,
            };
            break;
    }
    
    return {
        API: {
            GraphQL: {
                endpoint: storedData.AppsyncUrl,
                region: configData.AwsRegion,
                defaultAuthMode: AWS_APPSYNC_AUTH_TYPE_API_KEY,
                apiKey: configData.AppsyncApiKey,
            },
            REST: {
                [API_NAME]: {
                    endpoint: storedData.ApiGatewayUrl,
                    region: configData.AwsRegion,
                },
            }
        },
    };
};

export const configAmplify = (
    config: ResourcesConfig,
) => {
    const region = config.API?.GraphQL?.region || AWS_REGION_SE_2;
    
    Amplify.configure({
        ...config,
        Predictions: {
            convert: {
                translateText: {
                    region,
                },
            },
        },
    } as any, {
        API: {
            REST: {
                headers: async () => {
                    const authorizationHeader = await getApiAuthorizationHeader();
                    return {
                        Authorization: authorizationHeader,
                    };
                }
            }
        }
    });
    
    cognitoUserPoolsTokenProvider.setKeyValueStorage(new CookieStorage());
};

export const configPublicAmplify = (config: ResourcesConfig) => {
    Amplify.configure(config);
    cognitoUserPoolsTokenProvider.setKeyValueStorage(new CookieStorage());
};

const AMPLIFY_CONFIG_DEV = {
    environment: Environments.DEV,
    Auth: {
        Cognito: {
            userPoolId: USERPOOL_ID_DEV,
            userPoolClientId: USERPOOL_WEBCLIENT_ID_DEV,
        }
    },
    API: {
        GraphQL: {
            endpoint: APPSYNC_ENDPOINT_DEV,
            region: AWS_REGION_SE_2,
            defaultAuthMode: AWS_APPSYNC_AUTH_MODE,
        },
        REST: {
            [API_NAME]: {
                endpoint: API_ENDPOINT_DEV,
                region: AWS_REGION_SE_2,
            },
        }
        
    },
};

// eslint-disable-next-line
const AMPLIFY_CONFIG_TEST = {
    environment: Environments.TEST,
    Auth: {
        Cognito: {
            userPoolId: USERPOOL_ID_TEST,
            userPoolClientId: USERPOOL_WEBCLIENT_ID_TEST,
        }
    },
    API: {
        GraphQL: {
            endpoint: APPSYNC_ENDPOINT_TEST,
            region: AWS_REGION_SE_2,
            defaultAuthMode: AWS_APPSYNC_AUTH_MODE,
        },
        REST: {
            [API_NAME]: {
                endpoint: API_ENDPOINT_TEST,
                region: AWS_REGION_SE_2,
            },
        }
        
    },
};

 
const AMPLIFY_CONFIG_PRETEST = {
    environment: Environments.PRETEST,
    Auth: {
        Cognito: {
            userPoolId: USERPOOL_ID_PRETEST,
            userPoolClientId: USERPOOL_WEBCLIENT_ID_PRETEST,
        }
    },
    API: {
        GraphQL: {
            endpoint: APPSYNC_ENDPOINT_PRETEST,
            region: AWS_REGION_SE_2,
            defaultAuthMode: AWS_APPSYNC_AUTH_MODE,
        },
        REST: {
            [API_NAME]: {
                endpoint: API_ENDPOINT_PRETEST,
                region: AWS_REGION_SE_2,
            },
        }
        
    },
};

 
const AMPLIFY_CONFIG_PROD = {
    environment: Environments.PROD,
    Auth: {
        Cognito: {
            userPoolId: USERPOOL_ID_PROD,
            userPoolClientId: USERPOOL_WEBCLIENT_ID_PROD,
        }
    },
    API: {
        GraphQL: {
            endpoint: APPSYNC_ENDPOINT_PROD,
            region: AWS_REGION_SE_2,
            defaultAuthMode: AWS_APPSYNC_AUTH_MODE,
        },
        REST: {
            [API_NAME]: {
                endpoint: API_ENDPOINT_PROD,
                region: AWS_REGION_SE_2,
            },
        }
        
    },
};

const AMPLIFY_CONFIG_UAT = {
    environment: Environments.UAT,
    Auth: {
        Cognito: {
            userPoolId: USERPOOL_ID_UAT,
            userPoolClientId: USERPOOL_WEBCLIENT_ID_UAT,
        }
    },
    API: {
        GraphQL: {
            endpoint: APPSYNC_ENDPOINT_UAT,
            region: AWS_REGION_SE_2,
            defaultAuthMode: AWS_APPSYNC_AUTH_MODE,
        },
        REST: {
            [API_NAME]: {
                endpoint: API_ENDPOINT_UAT,
                region: AWS_REGION_SE_2,
            },
        }
        
    },
};

const AMPLIFY_CONFIG_NONPROD = {
    environment: Environments.NONPROD,
    Auth: {
        Cognito: {
            userPoolId: USERPOOL_ID_NONPROD,
            userPoolClientId: USERPOOL_WEBCLIENT_ID_NONPROD,
        }
    },
    API: {
        GraphQL: {
            endpoint: APPSYNC_ENDPOINT_NONPROD,
            region: AWS_REGION_SE_2,
            defaultAuthMode: AWS_APPSYNC_AUTH_MODE,
        },
        REST: {
            [API_NAME]: {
                endpoint: API_ENDPOINT_NONPROD,
                region: AWS_REGION_SE_2,
            },
        }
        
    },
};

/**
 * Public API configs
 */

export const AMPLIFY_PUBLIC_CONFIG_DEV = {
    API: {
        GraphQL: {
            endpoint: APPSYNC_ENDPOINT_DEV,
            region: AWS_REGION_SE_2,
            defaultAuthMode: AWS_APPSYNC_AUTH_TYPE_API_KEY,
            apiKey: 'da2-ryew6ojqzjcn5layh3li4sbwva',
        },
        REST: {
            [API_NAME]: {
                endpoint: API_ENDPOINT_DEV,
                region: AWS_REGION_SE_2,
            },
        }
        
    },
};

export const AMPLIFY_PUBLIC_CONFIG_TEST = {
    API: {
        GraphQL: {
            endpoint: APPSYNC_ENDPOINT_TEST,
            region: AWS_REGION_SE_2,
            defaultAuthMode: AWS_APPSYNC_AUTH_TYPE_API_KEY,
            apiKey: 'da2-76h7leap6vdo3dkjzqd6vx7yvi',
        },
        REST: {
            [API_NAME]: {
                endpoint: API_ENDPOINT_TEST,
                region: AWS_REGION_SE_2,
            },
        }
        
    },
};

export const AMPLIFY_PUBLIC_CONFIG_PRETEST = {
    API: {
        GraphQL: {
            endpoint: APPSYNC_ENDPOINT_PRETEST,
            region: AWS_REGION_SE_2,
            defaultAuthMode: AWS_APPSYNC_AUTH_TYPE_API_KEY,
            apiKey: 'da2-hg3ykejqaneftegrnalmx7oszi',
        },
        REST: {
            [API_NAME]: {
                endpoint: API_ENDPOINT_PRETEST,
                region: AWS_REGION_SE_2,
            },
        }
    },
};

export const AMPLIFY_PUBLIC_CONFIG_UAT = {
    API: {
        GraphQL: {
            endpoint: APPSYNC_ENDPOINT_UAT,
            region: AWS_REGION_SE_2,
            defaultAuthMode: AWS_APPSYNC_AUTH_TYPE_API_KEY,
            apiKey: 'da2-redmah7shbdktbwx2swkl6k6qa',
        },
        REST: {
            [API_NAME]: {
                endpoint: API_ENDPOINT_UAT,
                region: AWS_REGION_SE_2,
            },
        }
    },
};

export const AMPLIFY_PUBLIC_CONFIG_NONPROD = {
    API: {
        GraphQL: {
            endpoint: APPSYNC_ENDPOINT_NONPROD,
            region: AWS_REGION_SE_2,
            defaultAuthMode: AWS_APPSYNC_AUTH_TYPE_API_KEY,
            apiKey: 'da2-urzgcsehojhrplmbmpapqwmadq',
        },
        REST: {
            [API_NAME]: {
                endpoint: API_ENDPOINT_NONPROD,
                region: AWS_REGION_SE_2,
            },
        }
    },
};

export const AMPLIFY_PUBLIC_CONFIG_PROD = {
    API: {
        GraphQL: {
            endpoint: APPSYNC_ENDPOINT_PROD,
            region: AWS_REGION_SE_2,
            defaultAuthMode: AWS_APPSYNC_AUTH_TYPE_API_KEY,
            apiKey: 'da2-g2zfdd442bfo5mjm6q7epig5om',
        },
        REST: {
            [API_NAME]: {
                endpoint: API_ENDPOINT_PROD,
                region: AWS_REGION_SE_2,
            },
        }
    },
};

let defaultConfig: any = AMPLIFY_CONFIG_DEV; // defaults to AMPLIFY_CONFIG_DEV if REACT_APP_ENV is not defined

if (import.meta.env.VITE_STAGE === Environments.DEV) {
    defaultConfig = AMPLIFY_CONFIG_DEV;
} else if (import.meta.env.VITE_STAGE === Environments.PROD) {
    defaultConfig = AMPLIFY_CONFIG_PROD;
} else if (import.meta.env.VITE_STAGE === Environments.TEST) {
    defaultConfig = AMPLIFY_CONFIG_TEST;
} else if (import.meta.env.VITE_STAGE === Environments.UAT) {
    defaultConfig = AMPLIFY_CONFIG_UAT;
} else if (import.meta.env.VITE_STAGE === Environments.NONPROD) {
    defaultConfig = AMPLIFY_CONFIG_NONPROD;
} else if (import.meta.env.VITE_STAGE === Environments.PRETEST) {
    defaultConfig = AMPLIFY_CONFIG_PRETEST;
}

/**
 * Function that gets the current build environment.
 * Defined here to avoid circular dependency with commonFunctions.tsx
 */
const getCurrentBuildEnvironment = (): Environments => defaultConfig.environment;

export default defaultConfig;
